import type {PayloadAction} from '@reduxjs/toolkit'
import {createSlice, nanoid} from '@reduxjs/toolkit'

export type TToastType = 'success' | 'failure' | 'plain'

const DEFAULT_TOAST_DURATION_MS = 4000

export type TToast = {
  id: string
  type: TToastType
  message: string
  /** ms to display before auto-dismissing, or 'infinite' to require manual dismissal */
  duration: number | 'infinite'
}

export type ToastState = {
  toasts: TToast[]
}

const initialState: ToastState = {
  toasts: [],
}

const toastSlice = createSlice({
  name: 'toast',
  initialState,
  reducers: {
    addToast(state: ToastState, action: PayloadAction<TToast>) {
      const existingIndex = state.toasts.findIndex(t => t.id === action.payload.id)
      if (existingIndex >= 0) {
        state.toasts[existingIndex] = action.payload
      } else {
        state.toasts.push(action.payload)
      }
    },
    dismissToast(state: ToastState, action: PayloadAction<string>) {
      state.toasts = state.toasts.filter(t => t.id !== action.payload)
    },
    clearAllToasts(state: ToastState) {
      state.toasts = []
    },
  },
})

const {addToast, dismissToast, clearAllToasts} = toastSlice.actions

export type TShowToastOptions = {
  /**
   * A stable id, so the caller can later `dismissToast(id)` or replace this toast by showing
   * another with the same id. Auto-generated when omitted.
   */
  id?: string
  /** @default DEFAULT_TOAST_DURATION_MS. Use 'infinite' to require the user to dismiss it. */
  duration?: number | 'infinite'
}

const makeShowToast =
  (type: TToastType) => (message: string, options?: TShowToastOptions) =>
    addToast({
      id: options?.id ?? nanoid(),
      type,
      message,
      duration: options?.duration ?? DEFAULT_TOAST_DURATION_MS,
    })

export const showSuccessToast = makeShowToast('success')
export const showFailureToast = makeShowToast('failure')
export const showToast = makeShowToast('plain')

export {dismissToast, clearAllToasts}
export default toastSlice.reducer
